import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FiCheckCircle, FiLoader } from 'react-icons/fi';
import './EvaluationLink.css';

const EvaluationLink = () => {
  const { token } = useParams();
  const [formData, setFormData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({
    respondent_name: '',
    respondent_identifier: '',
    respondent_email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tagNumber, setTagNumber] = useState('');
  const [tagStatus, setTagStatus] = useState({ verifying: false, error: '', success: false });
  const [verifiedTrainee, setVerifiedTrainee] = useState(null);
  const [alreadyResponded, setAlreadyResponded] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/evaluations/link/${token}`);
        setFormData(response.data?.form || null);
        setQuestions(Array.isArray(response.data?.questions) ? response.data.questions : []);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired evaluation link');
      } finally {
        setLoading(false);
      }
    };

    fetchLink();
  }, [token]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const toggleMultiChoice = (questionId, option) => {
    setAnswers((prev) => {
      const existing = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      if (existing.includes(option)) {
        return { ...prev, [questionId]: existing.filter((item) => item !== option) };
      }
      return { ...prev, [questionId]: [...existing, option] };
    });
  };

  const handleVerifyTag = async () => {
    if (!tagNumber.trim()) {
      setTagStatus((prev) => ({ ...prev, error: 'Enter your tag number to continue.' }));
      return;
    }
    setTagStatus({ verifying: true, error: '', success: false });
    try {
      const response = await axios.post(`/api/evaluations/link/${token}/verify-tag`, {
        tagNumber: tagNumber.trim()
      });
      const trainee = response.data?.trainee;
      const hasResponded = response.data?.alreadyResponded === true;
      
      if (hasResponded) {
        setAlreadyResponded(true);
        setSubmittedAt(response.data?.submittedAt || null);
        setVerifiedTrainee(trainee || null);
        setMeta({
          respondent_identifier: trainee?.tag_number || '',
          respondent_name: `${trainee?.first_name || ''} ${trainee?.last_name || ''}`.trim(),
          respondent_email: trainee?.email || ''
        });
        setTagStatus({ verifying: false, error: '', success: true });
      } else {
        setAlreadyResponded(false);
        setVerifiedTrainee(trainee || null);
        setMeta({
          respondent_identifier: trainee?.tag_number || '',
          respondent_name: `${trainee?.first_name || ''} ${trainee?.last_name || ''}`.trim(),
          respondent_email: trainee?.email || ''
        });
        setTagStatus({ verifying: false, error: '', success: true });
      }
    } catch (error) {
      setVerifiedTrainee(null);
      setAlreadyResponded(false);
      setTagStatus({
        verifying: false,
        error: error.response?.data?.error || 'Unable to verify this tag number. Please try again.',
        success: false
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || submitted) return;
    if (!verifiedTrainee) {
      setError('Please verify your tag number to open the evaluation form.');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`/api/evaluations/link/${token}/respond`, {
        ...meta,
        respondent_tag: verifiedTrainee.tag_number,
        answers
      });
      setSubmitted(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to submit response at the moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="evaluation-link-page">
        <div className="evaluation-link-card loading-state">
          <FiLoader className="spin" />
          <p>Preparing evaluation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evaluation-link-page">
          <div className="evaluation-link-card error-state">
            <p>{error}</p>
          </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  return (
    <div className="evaluation-link-page">
      <form className="evaluation-link-card" onSubmit={handleSubmit}>
        {submitted ? (
          <div className="submission-state">
            <FiCheckCircle />
            <h2>Thank you for rating this session</h2>
            <p>Your response has been recorded. You may close this page.</p>
          </div>
        ) : (
          <>
            <header className="link-header">
              <div>
                <p className="eyebrow">Evaluation of Resource Person</p>
                <h1>{formData.course_title}</h1>
                <ul className="meta-list">
                  <li>
                    <strong>Resource Person:</strong> {formData.resource_person}
                  </li>
                  <li>
                    <strong>Date:</strong>{' '}
                    {formData.session_date
                      ? new Date(formData.session_date).toLocaleDateString()
                      : 'To be confirmed'}
                  </li>
                  <li>
                    <strong>Batch:</strong> {formData.batch_name || 'Not specified'}
                  </li>
                </ul>
              </div>
              <img
                src="/logo/css-farms-tvet-logo.png"
                alt="CSS FARMS IDEAS-TVET"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </header>

            {formData.description && <p className="form-description">{formData.description}</p>}

            <section className="section-card verify-card">
              <h3>Step 1 – Confirm your tag number</h3>
              <p>Only trainees assigned to this batch can fill the evaluation.</p>
              <div className="verify-row">
                <input
                  type="text"
                  value={tagNumber}
                  onChange={(e) => {
                    setTagNumber(e.target.value);
                    setTagStatus((prev) => ({ ...prev, error: '', success: false }));
                  }}
                  placeholder="Enter your tag / serial number"
                  disabled={tagStatus.verifying || !!verifiedTrainee}
                />
                <button
                  type="button"
                  className="btn primary"
                  onClick={handleVerifyTag}
                  disabled={tagStatus.verifying || !!verifiedTrainee}
                >
                  {tagStatus.verifying ? <FiLoader className="spin" /> : null}
                  {verifiedTrainee ? 'Verified' : 'Verify'}
                </button>
              </div>
              {tagStatus.error && <div className="error-banner subtle">{tagStatus.error}</div>}
              {verifiedTrainee && (
                <div className="success-banner">
                  Verified for {verifiedTrainee.first_name} {verifiedTrainee.last_name} (
                  {verifiedTrainee.tag_number})
                </div>
              )}
            </section>

            {verifiedTrainee && alreadyResponded ? (
              <section className="section-card already-responded-card">
                <div className="already-responded-content">
                  <FiCheckCircle className="already-responded-icon" />
                  <h3>You have already responded to this evaluation</h3>
                  <p>Thank you for your feedback. Your response has been recorded.</p>
                  {submittedAt && (
                    <p className="submitted-date">
                      Submitted on: {new Date(submittedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </section>
            ) : verifiedTrainee && !alreadyResponded ? (
              <>
                <section className="section-card identity-card">
                  <h3>Participant record</h3>
                  <p>This information is pulled from the trainee database and cannot be edited.</p>
                  <div className="identity-grid">
                    <div>
                      <span className="identity-label">Full Name</span>
                      <strong>{meta.respondent_name || '—'}</strong>
                    </div>
                    <div>
                      <span className="identity-label">Tag Number / Identifier</span>
                      <strong>{meta.respondent_identifier || '—'}</strong>
                    </div>
                    <div>
                      <span className="identity-label">Email</span>
                      <strong>{meta.respondent_email || '—'}</strong>
                    </div>
                  </div>
                </section>

                <section className="section-card">
                  <h3>Session questions</h3>
                  <div className="question-stack">
                    {questions.map((question, index) => (
                      <div key={question.id || index} className="question-block">
                        <div className="question-title">
                          <span>{index + 1}.</span>
                          <p>
                            {question.question_text}{' '}
                            {question.is_required && <sup className="required">*</sup>}
                          </p>
                        </div>

                        {['yes_no', 'single_choice', 'rating'].includes(question.question_type) && (
                          <div className="options-grid">
                            {(question.options || []).map((option) => (
                              <label key={option} className="option-pill">
                                <input
                                  type="radio"
                                  name={`question-${question.id}`}
                                  value={option}
                                  checked={answers[question.id] === option}
                                  onChange={() => handleAnswerChange(question.id, option)}
                                  required={question.is_required && !answers[question.id]}
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        )}

                        {question.question_type === 'multiple_choice' && (
                          <div className="options-grid">
                            {(question.options || []).map((option) => (
                              <label key={option} className="option-pill checkbox">
                                <input
                                  type="checkbox"
                                  value={option}
                                  checked={(answers[question.id] || []).includes(option)}
                                  onChange={() => toggleMultiChoice(question.id, option)}
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        )}

                        {question.question_type === 'text' && (
                          <textarea
                            rows="3"
                            value={answers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            required={question.is_required}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            ) : null}

            {!verifiedTrainee && (
              <div className="blocked-section">
                <p>Enter and verify your tag number to unlock the evaluation form.</p>
              </div>
            )}

            {error && <div className="error-banner">{error}</div>}

            {verifiedTrainee && !alreadyResponded && (
              <button
                className="btn primary submit-btn"
                type="submit"
                disabled={submitting || !verifiedTrainee}
              >
                {submitting ? (
                  <>
                    <FiLoader className="spin" /> Submitting...
                  </>
                ) : (
                  'Submit evaluation'
                )}
              </button>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default EvaluationLink;


